/**
 * Which host a signed-in user belongs on.
 *
 * `User.site` pins someone to a Site — the partner subdomain they registered on or
 * whose workspace they joined (docs/features/white-label-branding.md). The SPA's job
 * is to send them back to it whenever they land on a different host.
 *
 * Extracted from `router.beforeEach` so the bypass rules are testable: every branch
 * below is a case where getting it wrong is invisible in normal use. Too strict and an
 * advisor is silently never taken to their firm's subdomain; too loose and the page
 * rewrites itself to its own address on every navigation.
 *
 * Kept free of imports for the same reason as `services/advisoryPartner.js`: the unit
 * test imports this module directly, outside the app's module-alias/runtime setup.
 */

/**
 * Reduce a `Site.domain` to a bare, comparable hostname. `''` means "no usable target".
 *
 * The value is hand-typed in Django admin (step 3 of the new-subdomain runbook), so
 * `ABC.valur.com`, ` abc.valur.com`, `https://abc.valur.com` and `abc.valur.com/` all
 * turn up in practice. Compared raw against `location.hostname` every one of those
 * fails, and the only symptom is a redirect that never happens.
 *
 * Anything that doesn't reduce to a plausible hostname is rejected rather than
 * redirected to. That case is load-bearing: assigning an invalid value to
 * `URL.hostname` is *silently ignored*, so the "redirect" would resolve to the current
 * address, reload the page, and hit this guard again on the way back in.
 */
export function normalizeSiteHostname(value) {
    const raw = String(value ?? '')
        .trim()
        .toLowerCase();
    if (!raw) return '';
    const host = raw
        .replace(/^[a-z][a-z0-9+.-]*:\/\//, '') // scheme
        .split('/')[0] // path
        .split('@')
        .pop() // userinfo
        .split(':')[0]; // port
    const label = '[a-z0-9](?:[a-z0-9-]*[a-z0-9])?';
    return new RegExp(`^${label}(?:\\.${label})*$`).test(host) ? host : '';
}

/**
 * The hostname `user` should be redirected to, or `''` to stay put.
 *
 * Exemptions, both deliberate:
 * - **Staff and superusers** work across partner subdomains for support, so they are
 *   never moved. Worth knowing when testing this by hand on a Valur account — nothing
 *   will ever redirect it.
 * - **No Site** means nothing to redirect to. Either nobody ever pinned this user
 *   (`adopt_partner_site`), or their workspace's subdomain is only *reserved* — no DNS
 *   record and no cert SAN, so sending them there would land them on a dead host.
 *
 * Impersonation is handled by the caller, which has the store getter: an admin browsing
 * as someone else must not be thrown onto that person's host.
 */
export function siteRedirectTarget(user, currentHostname) {
    const { id, is_staff: isStaff, is_superuser: isSuperuser, site_domain: siteDomain } = user || {};
    if (!id || isStaff || isSuperuser) return '';
    const target = normalizeSiteHostname(siteDomain);
    if (!target || target === normalizeSiteHostname(currentHostname)) return '';
    return target;
}

/**
 * Leave for `target`, ending the session on the host being left (ENG-2839).
 *
 * The session here has to *end*, not merely be navigated away from. Auth cookies are
 * host-only (`api/auth_cookies.py`), so the target host receives none of them and the
 * user signs in there regardless — while a session left running here makes this host
 * bounce forever: `refreshJWT` restores it on boot *before* the first navigation
 * (`main.js`), so the guard fires again and nobody in this browser can reach this
 * host's login page until the refresh token expires. Signing out on the target host
 * doesn't help; it clears that host's cookies only. That is one account per browser
 * per host, with `/clear/` as the sole undocumented escape.
 *
 * `logout` is awaited, because assigning `location.href` cancels requests in flight —
 * an un-awaited one would clear the cookies on some loads and not others, and the bug
 * it lets back in surfaces on a later visit, nowhere near the change that caused it.
 *
 * It is awaited for no longer than `timeoutMs`, and its failures are swallowed. Both
 * because this runs *pre-mount* on a hard load: nothing is on screen yet, and there is
 * no global axios timeout, so waiting on a hung backend would strand the visitor on a
 * blank page. Giving up means only that the stale session survives — today's bug —
 * which beats never arriving anywhere.
 *
 * Dependencies are injected rather than imported for the reason in the module header:
 * the unit test imports this file directly, outside the app's runtime setup.
 */
export async function handOffToSite({ href, target, logout, navigate, timeoutMs = 3000 }) {
    const url = new URL(href);
    url.hostname = target;
    let timer;
    await Promise.race([
        Promise.resolve()
            .then(logout)
            .catch(() => {}),
        new Promise(resolve => {
            timer = setTimeout(resolve, timeoutMs);
        }),
    ]);
    clearTimeout(timer);
    navigate(url.toString());
}
